import * as ents from '@attain-apps/entities'

import type * as me from '../../../me'

/**
 * @see {@link ents.core.admin.User}
 * NOTE: 'cognitoId' is intentionally excluded as it is not intended for
 * frontend use.
 */
export type User = Omit<ents.core.admin.User.InContext, 'cognitoId'>

export namespace User {
  export const ACCESS_FLAGS_ATTRIBUTE_ID = 'accessFlags'

  const ACCESS_FLAGS = ents.core.admin.Access.Flag.WithLabel.get()

  export interface ProfileManagementParams {
    activeContext: ents.core.admin.Context | undefined
    currentUser: me.models.User | undefined
  }

  /**
   * User profile management is reserved for Platform Admin users and is exposed
   * only from the platform Users page or the tenant Users tab.
   */
  export function canManageProfile({
    activeContext,
    currentUser,
  }: ProfileManagementParams): boolean {
    return (
      Boolean(currentUser?.isPlatformAdmin) &&
      (activeContext === undefined || _isTenantContext(activeContext))
    )
  }

  /**
   * Returns a comma-separated label list for the access flags assigned to the
   * user in the current Users tab context.
   */
  export function getAccessFlagsLabel(user: User): string | undefined {
    const access = getRelevantAccess(user)

    if (!access || access <= 0) {
      return undefined
    }

    const labels = ACCESS_FLAGS.filter(
      flag =>
        user.isPlatformAdmin ??
        ents.core.admin.Access.allows(access, flag.value),
    ).map(flag => flag.label)

    return labels.length > 0 ? labels.join(', ') : undefined
  }

  /**
   * Identifies the tenant Users tab context.
   */
  function _isTenantContext(
    context: ents.core.admin.Context,
  ): context is ents.core.admin.Context.ForTenant {
    return !('zoneId' in context)
  }

  /**
   * Gets the relevant access level based on the user's assigned access types.
   * Priority order: tenant > zone > app > scenario
   */
  function getRelevantAccess(user: User): number | undefined {
    return (
      user.tenantAccess ??
      user.zoneAccess ??
      user.appAccess ??
      user.scenarioAccess
    )
  }
}
